/**
 * El modelo de la pantalla de Informes (modelo de Pablo, 08/2026): Informe = Ajustes + Plantilla.
 *
 * - `settings` son los AJUSTES (rojo): cuentas o líneas de control, período, comparación, filtros.
 *   Mismo shape snake_case que persiste el backend (`ClientReport._settings`).
 * - `template` es la PLANTILLA (violeta): `fields` (la PALETA: qué campos entran al informe) +
 *   `panels` (array ORDENADO). Todos los paneles del array son reales: agregar uno es un
 *   affordance de la grilla y no una tarjeta que ocupe lugar (card PlFYrmTo).
 *   La paleta es el universo sobre el que eligen los gráficos y del que cada TABLA toma su
 *   recorte: `panel.dimensions` / `panel.metrics` son propias de cada tabla y en su propio orden
 *   (card hwuk9PW7). Invariante: paleta ⊇ columnas de cada tabla — la sostiene el reducer.
 *
 * Todo lo de acá es puro: sin React, sin fetch. Es donde viven los tests.
 */

import {preferredChartDimension} from './chartFields';

export const PANEL_TYPES = Object.freeze({
    KPI: 'kpi',
    BARS: 'bars',
    PIE: 'pie',
    LINE: 'line',
    TABLE: 'table',
    // Bloque de texto (card 1YGcVgVw): insights y recomendaciones que escribe la agencia. No pide
    // datos: no entra al render ni a `planSignature`, y su contenido es HTML que sanea el backend.
    TEXT: 'text',
});

export const PERSISTED_PANEL_TYPES = Object.freeze([
    PANEL_TYPES.KPI, PANEL_TYPES.BARS, PANEL_TYPES.PIE, PANEL_TYPES.LINE, PANEL_TYPES.TABLE,
    PANEL_TYPES.TEXT,
]);

export function isTextPanel(panel) {
    return !!panel && panel.type === PANEL_TYPES.TEXT;
}

/** Los paneles que piden datos al motor: todos menos los bloques de texto. */
export function dataPanels(panels) {
    return (panels || []).filter((p) => !isTextPanel(p));
}

/**
 * El layout que se guarda en una PLANTILLA: los bloques de texto conservan su lugar y su título,
 * pero no lo escrito (decisión de fede del 24/10, review C-agregar-bloque-texto-informes-04). Lo
 * escrito son los insights de UN cliente y la plantilla se aplica a otros. El backend hace lo mismo
 * (`layout_schema.without_text_content`); acá se manda ya vacío para que la pantalla y lo guardado
 * digan lo mismo.
 */
export function withoutTextContent(layout) {
    return {
        ...layout,
        panels: (layout.panels || []).map((p) => (isTextPanel(p) ? {...p, content: ''} : p)),
    };
}

/**
 * ¿El HTML de un bloque dice algo? TipTap guarda `<p></p>` cuando se borra todo, y eso no es
 * contenido: un bloque así no se muestra en el portal ni en el PDF. Es una pregunta de presentación,
 * no de seguridad: el saneado vive en el backend y en `RichTextView`.
 */
export function textHasContent(html) {
    if (!html || typeof html !== 'string') return false;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}

export const MODES = Object.freeze({LIVE: 'LIVE', SNAPSHOT: 'SNAPSHOT'});

export const FIELD_KINDS = Object.freeze({DIMENSION: 'DIMENSION', METRIC: 'METRIC'});

export const DEFAULT_TIME_GRAIN = 'DAILY';
export const DEFAULT_PERIOD = Object.freeze({type: 'RELATIVE', predefined_period: 'LAST_30_DAYS'});
export const LAYOUT_VERSION = 1;

let uidSequence = 0;

export function newUid(prefix = 'p') {
    uidSequence += 1;
    return `${prefix}_${uidSequence}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Sólo para tests: ids predecibles. */
export function resetUidSequence() {
    uidSequence = 0;
}

export function emptySettings() {
    return {
        source_type: 'AD_ACCOUNTS',
        selected_items: [],
        period: {...DEFAULT_PERIOD},
        compare: false,
        compare_period: null,
        dimensions_filters: [],
    };
}

export function emptyTemplate() {
    return {id: null, name: null, is_shared: false, fields: [], panels: []};
}

/**
 * LA SEMILLA DE UN INFORME NUEVO (card 9VI3tLCQ).
 *
 * Hasta el 2026-09-14 la paleta nacía con cuatro campos (Campaña, Costo, Impresiones, Clicks) y
 * como la paleta es el universo sobre el que eligen TODOS los gráficos y tablas de la hoja, un
 * experto en paid que probaba la herramienta no veía CTR/CPC/CPM ni resultados sin ir al picker.
 *
 * Las dimensiones van en orden JERÁRQUICO, que es como se lee la tabla. Las métricas, en el orden
 * en que se miran: entrega, eficiencia, resultado y recién al final el revenue.
 *
 * `Anuncio` queda AFUERA a propósito: multiplica las filas y es `depends_on_daterange`. Está a un
 * clic en el modal.
 */
export const SEED_DIMENSIONS = Object.freeze(['Canal', 'Cuenta', 'Campana', 'Grupo_de_Anuncios']);

/** Entrega y eficiencia: resuelven en cualquier canal, con o sin tracking. */
export const SEED_DELIVERY_METRICS = Object.freeze(['Costo', 'Impresiones', 'Clicks', 'CTR', 'CPC', 'CPM']);

/**
 * El resultado: la familia de «Eventos seleccionados», NO la de «Conversiones primarias» (card
 * RsuG69eR, reunión del 17/09).
 *
 * ⚠️ **Hasta el 24/09 era al revés, y a propósito.** La semilla del 14/09 eligió las primarias
 * porque las de eventos están en `EVENT_DEPENDENT_FIELDS` y se creyó que darían columnas vacías al
 * 100% de los clientes nuevos. La premisa era falsa, y no la repongas sin leer esto:
 * - En **Google**, una cuenta sin eventos elegidos tiene `use_all_primary_conversions=True` y el
 *   backend manda `action_ids=None` (`looker_cc/commands/get_metrics_of_ad_accounts.py`): los
 *   eventos seleccionados SON las primarias. Mismo número que antes.
 * - En **Meta** las primarias no existen (`has_conversions_metric: False`) y daban CERO SIEMPRE,
 *   sin forma de arreglarlo. Los eventos dan el dato real, o cero hasta que la agencia los elige —
 *   y ese cero ya no es mudo: el aviso de arriba del folio y el ícono de cada bloque lo dicen y
 *   abren el modal para elegirlos (card UvIR4eHe, `eventsCheck.js`).
 * - **TikTok y LinkedIn SÍ tienen primarias** (`has_conversions_metric: True`): se comportan como
 *   Google (review C-INFCONV-05; un comentario anterior decía lo contrario).
 */
export const SEED_EVENT_METRICS = Object.freeze([
    'Eventos_Seleccionados', 'Conv_Rate_Eventos_seleccionados', 'CPA_Eventos_seleccionados',
]);

/**
 * La semilla genérica: entrega + eficiencia + resultado. Es también el orden de las COLUMNAS de la
 * tabla, y sin tipo de negocio sus tres primeras son las métricas destacadas.
 */
export const SEED_CORE_METRICS = Object.freeze([...SEED_DELIVERY_METRICS, ...SEED_EVENT_METRICS]);

/**
 * Las que dependen de que haya tracking de valor. Van a la paleta Y a la tabla, como el resto.
 *
 * ⚠️ **Estuvieron FUERA de la tabla sembrada entre el 14 y el 15/09, y el QA sobre producción dio
 * vuelta esa decisión.** Sin saber el tipo de negocio, sacarlas le quitaba a un ecommerce sobre
 * Meta sus únicas métricas de resultado. Desde el 24/09 el recorte existe, pero SÓLO cuando el
 * perfil del cliente dice que no vende (lead gen, app, branding): ver `SEED_BY_BUSINESS_TYPE`. Sin
 * perfil, van.
 */
export const SEED_REVENUE_METRICS = Object.freeze(['ROAS', 'Valor_de_compra']);

/** Los valores de `AgencyClientProfile._business_type` (backend). */
export const BUSINESS_TYPES = Object.freeze({
    ECOMMERCE: 'ECOMMERCE',
    LEAD_GEN: 'LEAD_GEN',
    BRAND_AWARENESS: 'BRAND_AWARENESS',
    APP_INSTALL: 'APP_INSTALL',
    OTHER: 'OTHER',
});

const GENERIC_SEED = Object.freeze({
    metrics: [...SEED_CORE_METRICS, ...SEED_REVENUE_METRICS],
    kpis: ['Costo', 'Impresiones', 'Clicks'],
});

const RESULTS_SEED = Object.freeze({
    metrics: [...SEED_CORE_METRICS],
    kpis: ['Costo', 'Eventos_Seleccionados', 'CPA_Eventos_seleccionados'],
});

/**
 * LA SEMILLA POR TIPO DE NEGOCIO (fede, 24/09). Cierra la decisión que la semilla genérica dejaba
 * abierta (review C-02): un default que le sirva a todos le mete columnas en cero a alguien. El
 * tipo sale del perfil del cliente (Configuración → Contexto del negocio), que ya se usa para no
 * sugerirle Shopify a un lead gen (`PlatformsBox.js`).
 *
 * - `metrics` es la paleta, en el orden de las columnas de la tabla (entrega → resultado → revenue).
 * - `kpis` son las tres métricas destacadas. Van APARTE a propósito: si se eligieran reordenando
 *   `metrics`, las columnas de la tabla quedarían desordenadas.
 *
 * Un tipo que no está acá (OTHER, sin cargar, error al pedirlo) cae en `GENERIC_SEED`.
 */
export const SEED_BY_BUSINESS_TYPE = Object.freeze({
    [BUSINESS_TYPES.ECOMMERCE]: {
        metrics: [...SEED_CORE_METRICS, ...SEED_REVENUE_METRICS],
        kpis: ['Costo', 'Valor_de_compra', 'ROAS'],
    },
    [BUSINESS_TYPES.LEAD_GEN]: RESULTS_SEED,
    [BUSINESS_TYPES.APP_INSTALL]: RESULTS_SEED,
    // Branding no convierte: las de eventos serían tres columnas en cero que nadie mira.
    [BUSINESS_TYPES.BRAND_AWARENESS]: {
        metrics: [...SEED_DELIVERY_METRICS],
        kpis: ['Costo', 'Impresiones', 'CPM'],
    },
});

export function seedFor(businessType) {
    return SEED_BY_BUSINESS_TYPE[businessType] || GENERIC_SEED;
}

/**
 * La dimensión con la que nacen las barras y la torta de un informe nuevo.
 *
 * Sin esto caerían en la PRIMERA graficable de la paleta —`preferredChartDimension`, card
 * PlFYrmTo—, que con el orden jerárquico es «Plataforma»: para un cliente monocanal, una torta de
 * una sola porción. El sesgo vive acá y no en `preferredChartDimension` a propósito: allá cambiaría
 * también los paneles que se agregan sobre una plantilla YA guardada, que no es lo que pide esta
 * card. Como contrapartida, una torta agregada a mano después nace en la primera de la paleta.
 */
export const SEED_CHART_DIMENSION = 'Campana';

/**
 * Los campos con los que arranca un informe nuevo, recortados a lo que la FUENTE admite.
 *
 * Se filtra por `FIELDS_BY_DATA_SOURCE_TYPE[sourceType]` y no por el catálogo entero: sin eso el
 * fallback («las primeras del catálogo» cuando no hay ninguna preferida) podía elegir campos que
 * la fuente activa no acepta. Sin `sourceType` se comporta como antes y mira todo el catálogo.
 * `businessType` elige la paleta (`seedFor`); sin él, la genérica.
 */
export function defaultFieldsFrom(fieldsData, sourceType = null, businessType = null) {
    if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_SEGMENTS) return [];
    const {FIELD_DATA_BY_ID, FIELD_SEGMENTS, FIELDS_BY_DATA_SOURCE_TYPE} = fieldsData;
    const pool = (sourceType && FIELDS_BY_DATA_SOURCE_TYPE && FIELDS_BY_DATA_SOURCE_TYPE[sourceType])
        || Object.keys(FIELD_DATA_BY_ID);
    const ofSegment = (segment) => pool.filter((id) => FIELD_DATA_BY_ID[id] && FIELD_DATA_BY_ID[id].segment === segment);
    const dims = ofSegment(FIELD_SEGMENTS.DIMENSIONS);
    const mets = ofSegment(FIELD_SEGMENTS.METRICS);
    // El orden lo fija la lista preferida, no el catálogo: es el que va a leer la tabla.
    const pick = (preferred, available, n) => {
        const found = preferred.filter((id) => available.includes(id));
        return found.length ? found : available.slice(0, n);
    };
    return [
        ...pick(SEED_DIMENSIONS, dims, 1).map((name) => ({name, kind: FIELD_KINDS.DIMENSION})),
        ...pick(seedFor(businessType).metrics, mets, 3).map((name) => ({name, kind: FIELD_KINDS.METRIC})),
    ];
}

/** Las métricas destacadas del tipo de negocio, para `defaultPanels`. */
export function defaultKpisFor(businessType) {
    return [...seedFor(businessType).kpis];
}

export function emptyState(agencyClientId = null) {
    return {
        agencyClientId,
        informe: null,
        settings: emptySettings(),
        template: emptyTemplate(),
        baseline: {informe: null, template: null},
        undoStack: [],
        accountsResetAt: null,
    };
}

export function metricNames(fields) {
    return (fields || []).filter((f) => f.kind === FIELD_KINDS.METRIC).map((f) => f.name);
}

export function dimensionNames(fields) {
    return (fields || []).filter((f) => f.kind === FIELD_KINDS.DIMENSION).map((f) => f.name);
}

/**
 * Las columnas que MUESTRA una tabla: su recorte propio, o la paleta entera si no lo tiene.
 *
 * El fallback existe porque `layout_schema._normalized_panel` sólo emite `dimensions`/`metrics`
 * cuando no son None: una tabla guardada antes de la card hwuk9PW7 —o creada por el backend—
 * puede no traerlas. Vive acá y no repetido en cada consumidor: lo necesitan el serializador, el
 * rótulo del panel y el modal que las edita, y con tres copias la que se olvide muestra la tabla
 * vacía.
 */
export function tableColumnsOf(panel, fields) {
    return {
        dimensions: (panel && panel.dimensions) || dimensionNames(fields),
        metrics: (panel && panel.metrics) || metricNames(fields),
    };
}

/**
 * Un panel nuevo del tipo pedido: la primera métrica y la primera dimensión GRAFICABLE.
 *
 * Antes tomaba la ÚLTIMA dimensión —el comentario decía «las primeras» y el código hacía otra
 * cosa—, así que con «Meta - Anuncios» la torta nacía en Imagen/video y las URLs desbordaban la
 * tarjeta por encima del selector (card PlFYrmTo). El criterio vive en `chartFields.js`.
 */
export function defaultPanel(type, {metrics = [], dimensions = [], fieldsData = null} = {}) {
    const uid = newUid();
    const base = {uid, id: uid, type, title: ''};
    const firstMetric = metrics[0] || null;
    const chartDimension = preferredChartDimension(dimensions, fieldsData);
    switch (type) {
        case PANEL_TYPES.KPI:
            return {...base, metrics: firstMetric ? [firstMetric] : []};
        case PANEL_TYPES.BARS:
        case PANEL_TYPES.PIE:
            return {...base, dimension: chartDimension, metrics: firstMetric ? [firstMetric] : []};
        case PANEL_TYPES.LINE:
            return {...base, metrics: firstMetric ? [firstMetric] : [], time_grain: DEFAULT_TIME_GRAIN};
        case PANEL_TYPES.TABLE:
            // Una tabla nace mostrando toda la paleta; desde ahí se recorta con su propio
            // «Editar», sin tocar a las demás tablas de la hoja (card hwuk9PW7).
            return {...base, dimensions: [...dimensions], metrics: [...metrics]};
        case PANEL_TYPES.TEXT:
            return {...base, content: ''};
        default:
            throw new Error(`Tipo de panel desconocido: ${type}`);
    }
}

/**
 * `DEFAULT_PANELS` del mockup (gráficos dinámicos, línea 780): 3 métricas destacadas, barras,
 * torta, evolución y la tabla.
 *
 * ⚠️ **El backend calca esto salvo en UNA cosa.**
 * `reports/services/layout_schema.py::default_panels_layout` comparte el orden, los spans, los
 * fallbacks y —desde que se sacó el recorte del revenue, 15/09— también las columnas de la tabla.
 * Lo único que sigue divergiendo es el eje del gráfico: allá es la primera dimensión graficable
 * (que con la paleta nueva es «Plataforma»), acá es `SEED_CHART_DIMENSION`. No choca con nada
 * (`LOAD_INFORME` y `APPLY_TEMPLATE` no pasan por acá; el único llamador es `NEW_INFORME`), pero
 * un layout nacido allá nace con la torta en otra dimensión (review C-05).
 *
 * Los 3 KPIs son `kpiMetrics` (las del tipo de negocio, `defaultKpisFor`) que estén en la paleta;
 * sin esa lista, o si la paleta no trae ninguna, las tres primeras de la paleta (review C-07).
 */
export function defaultPanels(fields, fieldsData = null, kpiMetrics = null) {
    const metrics = metricNames(fields);
    const dimensions = dimensionNames(fields);
    const ctx = {metrics, dimensions, fieldsData};
    // Los gráficos miran la paleta con `SEED_CHART_DIMENSION` adelante. La tabla la lleva ENTERA.
    const chartCtx = {...ctx, dimensions: seedChartDimensions(dimensions)};
    const preferred = (kpiMetrics || []).filter((m) => metrics.includes(m));
    const kpiList = preferred.length ? preferred.slice(0, 3) : metrics.slice(0, 3);
    const kpis = kpiList.map((metric) => ({...defaultPanel(PANEL_TYPES.KPI, ctx), metrics: [metric]}));
    return [
        ...kpis,
        defaultPanel(PANEL_TYPES.BARS, chartCtx),
        defaultPanel(PANEL_TYPES.PIE, chartCtx),
        defaultPanel(PANEL_TYPES.LINE, ctx),
        defaultPanel(PANEL_TYPES.TABLE, ctx),
    ];
}

/** La paleta con la dimensión preferida adelante, para que el gráfico nazca ahí. */
export function seedChartDimensions(dimensions) {
    const list = dimensions || [];
    if (!list.includes(SEED_CHART_DIMENSION)) return list;
    return [SEED_CHART_DIMENSION, ...list.filter((d) => d !== SEED_CHART_DIMENSION)];
}

function persistedPanel(panel, fields) {
    const common = {id: panel.id || panel.uid, type: panel.type, title: panel.title || ''};
    // `options` (mostrar la variación, agrupar el resto de la torta, totales de la tabla) y
    // `col_span` viajan TAL CUAL, sin interpretarlos. El editor no los ofrece todavía, pero el
    // backend SÍ los emite —las plantillas que salen de un set de columnas nacen con ellos— y sin
    // esto la primera edición desde la pantalla los borraba en silencio: el validador acepta el
    // layout sin ellos, así que no había ningún error que lo delatara (review C-editor-06).
    if (panel.options) common.options = panel.options;
    if (panel.col_span != null) common.col_span = panel.col_span;
    switch (panel.type) {
        case PANEL_TYPES.KPI:
            return {...common, metrics: (panel.metrics || []).slice(0, 1)};
        case PANEL_TYPES.BARS:
            return {...common, dimension: panel.dimension, metrics: panel.metrics || []};
        case PANEL_TYPES.PIE:
            return {...common, dimension: panel.dimension, metrics: (panel.metrics || []).slice(0, 1)};
        case PANEL_TYPES.LINE:
            return {...common, metrics: panel.metrics || [], time_grain: panel.time_grain || DEFAULT_TIME_GRAIN};
        case PANEL_TYPES.TABLE:
            // Cada tabla lleva SU recorte de la paleta, en el orden en que se eligió. Hasta el
            // 2026-09-09 acá se emitían los campos de la HOJA, así que N tablas eran siempre
            // clones: agregar una dimensión en una las cambiaba todas (card hwuk9PW7). El
            // fallback cubre los layouts sin columnas propias — `layout_schema` las deja
            // opcionales, así que una tabla persistida puede no traerlas.
            return {...common, ...tableColumnsOf(panel, fields)};
        case PANEL_TYPES.TEXT:
            return {...common, content: panel.content || ''};
        default:
            // Un tipo que este bundle no conoce (uno que sume el backend más adelante) vuelve TAL
            // CUAL lo trajo el layout. Hasta el bloque de texto, acá se devolvía `null` y el panel
            // se borraba en silencio al guardar: el validador acepta el layout sin él, así que una
            // pestaña con el bundle viejo se comía los bloques nuevos sin ningún error.
            return panel.raw ? {...panel.raw} : null;
    }
}

/** El layout tal como lo guarda el backend. */
export function serializeLayout(template) {
    const fields = template.fields || [];
    const panels = template.panels || [];
    return {
        version: LAYOUT_VERSION,
        metrics: metricNames(fields),
        dimensions: dimensionNames(fields),
        panels: panels.map((p) => persistedPanel(p, fields)).filter(Boolean),
    };
}

/**
 * Lo inverso: del layout persistido al estado editable.
 *
 * `add_index` se IGNORA. Los layouts guardados hasta el 07/09 lo traen (era dónde estaba la
 * tarjeta «Agregar») y se leen igual, sin migrar nada: el backend lo sigue aceptando como opcional
 * y el dirty se compara sólo sobre `panels`, así que un informe viejo no queda sucio al abrirlo.
 */
export function templateStateFromLayout(layout, {id = null, name = null, is_shared = false} = {}) {
    const dims = (layout && layout.dimensions) || [];
    const mets = (layout && layout.metrics) || [];
    const fields = [
        ...dims.map((name) => ({name, kind: FIELD_KINDS.DIMENSION})),
        ...mets.map((name) => ({name, kind: FIELD_KINDS.METRIC})),
    ];
    const panels = ((layout && layout.panels) || []).map((p) => ({
        uid: p.id || newUid(),
        id: p.id || newUid(),
        type: p.type,
        title: p.title || '',
        dimension: p.dimension,
        // `dimensions` (plural) son las columnas de una TABLA; `dimension` (singular) el eje de
        // un gráfico. Leerlas es lo que hace que cada tabla conserve su recorte al reabrir el
        // informe: hasta el 2026-09-09 se descartaban y se re-derivaban de la hoja (hwuk9PW7).
        dimensions: p.dimensions ? [...p.dimensions] : undefined,
        metrics: p.metrics ? [...p.metrics] : undefined,
        time_grain: p.time_grain,
        // Se leen para poder devolverlos: ver `persistedPanel` (review C-editor-06).
        options: p.options,
        col_span: p.col_span,
        // El HTML del bloque de texto, ya saneado por el backend.
        content: p.type === PANEL_TYPES.TEXT ? (p.content || '') : undefined,
        // Un tipo desconocido se guarda entero para devolverlo igual (ver `persistedPanel`).
        raw: PERSISTED_PANEL_TYPES.includes(p.type) ? undefined : JSON.parse(JSON.stringify(p)),
    }));
    return {id, name, is_shared, fields, panels};
}
